import React, { useContext } from 'react';
import { bool, string } from 'prop-types';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';

// Components
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

// Context
import { SequencingWizardContext } from '../SequencingWizardContext';

const iconStyle = {
  '& svg': {
    margin: 0,
  },
  color: 'secondary.main',
  minWidth: 'unset',
  padding: 0,
};

const row = {
  '&:last-of-type': {
    borderBottom: 'none',
  },
  borderBottom: '1px solid',
  borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  px: 3,
  py: 2,
};

const subtitleStyle = {
  backgroundColor: 'graphic.light',
  borderTop: '1px solid',
  borderTopColor: 'rgba(0, 0, 0, 0.08)',
  px: 3,
  py: 1,
  width: '100%',
};

const SummaryDrawer = props => {
  const { displayProducts, displayProductLocations, inventoryOwnerSubtitle } = props;
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only('xs'), { noSsr: true });

  const {
    data: { changeList, inventoryOwners, products },
    openSummary,
    setOpenSummary,
  } = useContext(SequencingWizardContext);

  const renderInventoryOwners = () => (
    <>
      <Typography sx={subtitleStyle} variant="subtitle1">
        {`${inventoryOwnerSubtitle}${inventoryOwners.length > 1 ? 's' : ''}`}
      </Typography>
      {inventoryOwners.map(({ companyCustomerIdName: id, nameLong }) => (
        <Typography key={id} sx={row} variant="body1">
          {`${id} ${nameLong}`}
        </Typography>
      ))}
    </>
  );
  const renderProducts = () => (
    <>
      <Typography sx={subtitleStyle} variant="subtitle1">
        Updating Sequences
      </Typography>
      {products.map(({ id, name }) => (
        <Typography key={id} sx={row} variant="body1">
          {name}
        </Typography>
      ))}
    </>
  );
  const renderProductLocations = () => (
    <>
      <Typography sx={subtitleStyle} variant="subtitle1">
        Updating Sequences
      </Typography>
      {changeList.map(({ location, product }, index) => (
        <Typography
          color="text.primary"
          // eslint-disable-next-line react/no-array-index-key
          key={`${location.nameShort}-${product.nameShort}-${index}`}
          sx={row}
          variant="body1"
        >
          {product.nameShort} at {location.nameShort}
        </Typography>
      ))}
    </>
  );

  return (
    <>
      <Box
        alignItems="flex-start"
        bgcolor="graphic.lighter"
        borderTop={`0.1em solid ${theme.palette.graphic.light}`}
        bottom={68}
        display="flex"
        flexDirection="column"
        height={openSummary ? `calc(100% - 231px)` : '48px'}
        overflow={openSummary ? 'visible overlay' : 'visible hidden'}
        position="fixed"
        sx={{
          scrollbarWidth: 'none',
          transition: '0.5s ease-in-out',
        }}
        width={xs ? '100%' : `390px`}
        zIndex={2}
      >
        {/* Button instead of a div for accessibility and testing reasons */}
        <Button
          onClick={() => setOpenSummary(!openSummary)}
          sx={{
            '&:hover': {
              backgroundColor: 'initial',
            },
            alignItems: 'center',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: 48,
            padding: theme.spacing(0, 3),
            width: '100%',
            zIndex: 4,
          }}
          variant="text"
        >
          <Typography color="primary" variant="subtitle1">
            Summary
          </Typography>
          {openSummary ? (
            <ExpandMoreRounded sx={iconStyle} />
          ) : (
            <ExpandLessRounded sx={iconStyle} />
          )}
        </Button>
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          sx={{
            overflow: 'auto',
          }}
          width={1}
        >
          <Box width={1}>{renderInventoryOwners()}</Box>
          <Box width={1}>{displayProducts && renderProducts()}</Box>
          <Box width={1}>{displayProductLocations && renderProductLocations()}</Box>
        </Box>
      </Box>
    </>
  );
};

SummaryDrawer.propTypes = {
  displayProductLocations: bool,
  displayProducts: bool,
  inventoryOwnerSubtitle: string.isRequired,
};

SummaryDrawer.defaultProps = {
  displayProductLocations: false,
  displayProducts: false,
};

export default SummaryDrawer;
